import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { MoleculeThumbnail } from './MoleculeThumbnail';
import { IsomerThumbnail } from './IsomerThumbnail';
import { ReactionThumbnail } from './ReactionThumbnail';
import { getIllustration } from '@peruselab/api-client/illustrations';
import { getIsomer } from '@peruselab/api-client/isomers';
import type { IllustrationElement } from '@peruselab/structure-editor/domain/illustrationElement';

interface IllustrationViewerProps {
  readonly illustrationId: string;
  readonly onBack: () => void;
}

interface ResolvedElement {
  readonly key: string;
  readonly label: string | undefined;
  readonly thumbnail: ReactNode;
}

type ViewerState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly title: string | null; readonly elements: readonly ResolvedElement[] }
  | { readonly status: 'error'; readonly message: string };

async function resolveElement(element: IllustrationElement): Promise<ResolvedElement> {
  if (element.entityType === 'isomer') {
    const isomer = await getIsomer(element.id);
    return {
      key: `isomer-${element.id}`,
      label: element.label,
      thumbnail: <IsomerThumbnail moleculeId={isomer.moleculeId} isomerId={element.id} size={96} />
    };
  }
  if (element.entityType === 'molecule') {
    return {
      key: `molecule-${element.id}`,
      label: element.label,
      thumbnail: <MoleculeThumbnail moleculeId={element.id} size={96} />
    };
  }
  return {
    key: `reaction-${element.id}`,
    label: element.label,
    thumbnail: <ReactionThumbnail reactionId={element.id} />
  };
}

// #523: a read-only view of an already-saved Illustration, opened from a search result card.
// Not a reuse of IllustrationComposer -- that owns pickers, an interactive placement canvas, and
// a create-only save action, none of which apply here. Renders each element's thumbnail in a
// plain grid rather than reproducing IllustrationCanvas's exact x/y placement: faithful spatial
// layout would mean pulling in most of that canvas's geometry for a read-only pass whose only job
// is "show me what's in this illustration," not "show me exactly how it was arranged."
export function IllustrationViewer({ illustrationId, onBack }: IllustrationViewerProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<ViewerState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      const illustration = await getIllustration(illustrationId);
      const elements = await Promise.all(illustration.document.elements.map(resolveElement));
      if (!cancelled) setState({ status: 'ready', title: illustration.title, elements });
    })().catch((error: unknown) => {
      if (!cancelled) {
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : t('illustrationViewer.loadError')
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [illustrationId, t]);

  return (
    <section className="reaction-composer illustration-composer" aria-labelledby="illustration-viewer-heading">
      <div className="reaction-composer-header">
        <h2 id="illustration-viewer-heading">
          {state.status === 'ready' && state.title ? state.title : t('illustrationViewer.heading')}
        </h2>
        <button
          type="button"
          className="icon-button"
          onClick={onBack}
          aria-label={t('illustrationComposer.backToEditor')}
          title={t('illustrationComposer.backToEditor')}
        >
          <Icon name="arrowBack" />
        </button>
      </div>

      {state.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
      {state.status === 'error' ? (
        <p className="import-error" role="alert">
          {state.message}
        </p>
      ) : null}

      {state.status === 'ready' ? (
        <ul className="illustration-viewer-grid">
          {state.elements.map((element) => (
            <li key={element.key} className="illustration-viewer-element">
              {element.thumbnail}
              {element.label ? <span className="illustration-viewer-element-label">{element.label}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
